import type { ProductType, Prompt } from "@1js/pl-card-components";
import React from "react";
import { type HandoffErrorsType } from "../../hooks/handoff.types";
import { useFeatureEnablement } from "../../hooks/useFeatureEnablement";
import { useHandoffService } from "../../hooks/useHandoffService";
import {
  recentFilesByProductAsync,
  type HandoffFile,
  type RecentFiles,
} from "../../services/workingsetfiles-client.web";
import useTelemetry from "../../utils/Telemetry/useTelemetry";
import { type HandoffFileSelectionMenuConfiguration } from "../HandoffFileSelectionMenu/HandoffFileSelectionMenu.web";
import type { LoaderStateType } from "./Loader/Loader.web.hook";

export type HandoffProps = {
  serviceHost: string;
  locale: string;
};

export type LoaderConfigurationType =
  | {
      prompt: Prompt;
      destination: ProductType | string;
      state: LoaderStateType;
      error: HandoffErrorsType;
      abortController?: AbortController;
    }
  | undefined;

export type HandoffResult = {
  recentFiles: RecentFiles;
  onHandoffFileSelectionMenuOpenChange: () => void;
  handoffFileSelectionMenuConfiguration: HandoffFileSelectionMenuConfiguration;
  onLoaderCancel: () => void;
  onLoaderCopy: () => void;
  loaderConfiguration:
    | {
        prompt: Prompt;
        destination: ProductType | string;
        state: LoaderStateType;
      }
    | undefined;
  onTryInAppBadgeAction: (
    prompt: Prompt,
    product: ProductType | string,
    tagRef: React.MutableRefObject<null>
  ) => void;
  onTryByCopyBadgeAction: (
    prompt: Prompt,
    product: ProductType | string,
    tagRef: React.MutableRefObject<null>
  ) => void;
};

export const useHandoff = (props: HandoffProps): HandoffResult => {
  const { serviceHost, locale } = props;
  const telemetry = useTelemetry();
  const [loaderConfiguration, setLoaderConfiguration] =
    React.useState<LoaderConfigurationType>(undefined);
  const onSuccess = React.useCallback(() => {
    setLoaderConfiguration(undefined);
  }, [setLoaderConfiguration]);

  const onError = React.useCallback(
    (prompt: Prompt, destination: string, error: HandoffErrorsType) => {
      telemetry.sendTryInAppEventError(prompt, destination, error);
      setLoaderConfiguration((config: LoaderConfigurationType) => {
        return {
          ...config,
          prompt,
          destination,
          state: "error",
          error: error as HandoffErrorsType,
        } as LoaderConfigurationType;
      });
    },
    [telemetry, setLoaderConfiguration]
  );

  const { handoff, isProductNeedsFile } = useHandoffService();

  const openLoader = React.useCallback(
    (
      prompt: Prompt,
      product: ProductType | string,
      state: LoaderStateType,
      abortController?: AbortController
    ) => {
      telemetry.sendTryInAppEventLoading(prompt, product);
      setLoaderConfiguration((_) => {
        return {
          prompt,
          destination: product,
          state,
          error: "UnknownError",
          abortController,
        };
      });
    },
    [telemetry, setLoaderConfiguration]
  );

  const onTryInAppBadgeAction = React.useCallback(
    async (
      prompt: Prompt,
      product: ProductType | string,
      tagRef: React.MutableRefObject<null>
    ) => {
      if (!isProductNeedsFile(product)) {
        const abortController = new AbortController();
        openLoader(prompt, product, "loading", abortController);
        try {
          await handoff({
            host: serviceHost,
            destination: product,
            locale: locale,
            prompt,
            abortController,
          });

          if (!abortController.signal.aborted) {
            telemetry.sendTryInAppEventSuccess(prompt, product);
            onSuccess();
          }
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
          onError(prompt, product, (error as any).message);
        }
        return;
      }
      setHandoffFileSelectionMenuConfiguration({
        isOpen: true,
        product: product,
        openRef: tagRef,
        onAction: async (file: HandoffFile | undefined) => {
          const abortController = new AbortController();
          openLoader(prompt, product, "loading", abortController);
          try {
            await handoff({
              host: serviceHost,
              destination: product,
              locale: locale,
              prompt,
              file,
              abortController,
            });

            if (!abortController.signal.aborted) {
              telemetry.sendTryInAppEventSuccess(prompt, product);
              onSuccess();
            }
          } catch (error) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
            onError(
              prompt,
              product,
              (error as Error).message as HandoffErrorsType
            );
          }
          return;
        },
      });
    },
    [
      isProductNeedsFile,
      openLoader,
      handoff,
      serviceHost,
      locale,
      telemetry,
      onSuccess,
      onError,
    ]
  );

  const onTryByCopyBadgeAction = React.useCallback(
    (prompt: Prompt, product: ProductType | string) => {
      openLoader(prompt, product, "copy", undefined);
      return;
    },
    [openLoader]
  );

  const [
    handoffFileSelectionMenuConfiguration,
    setHandoffFileSelectionMenuConfiguration,
  ] = React.useState<HandoffFileSelectionMenuConfiguration>({
    product: "",
    isOpen: false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onAction: () => {},
    openRef: React.useRef(null),
  });

  const onHandoffFileSelectionMenuOpenChange = React.useCallback(() => {
    setHandoffFileSelectionMenuConfiguration((prev) => ({
      ...prev,
      isOpen: !prev.isOpen,
    }));
  }, []);

  const [recentFiles, setRecentFiles] = React.useState<RecentFiles>({
    Word: [],
    Excel: [],
    PowerPoint: [],
  });

  const { isTryInRetrieveWorkingFiles, isUseAccessUrlEnabled } =
    useFeatureEnablement();
  React.useEffect(() => {
    if (!isTryInRetrieveWorkingFiles) {
      return;
    }
    recentFilesByProductAsync(serviceHost, locale, isUseAccessUrlEnabled).then(
      (files) => setRecentFiles((_) => files)
    );
  }, [locale, serviceHost, isTryInRetrieveWorkingFiles, isUseAccessUrlEnabled]);

  const onLoaderCancel = React.useCallback(() => {
    if (!loaderConfiguration) {
      return;
    }

    if (loaderConfiguration.abortController) {
      loaderConfiguration.abortController.abort();
    }

    telemetry.sendTryInAppEventCancelled(
      loaderConfiguration.prompt,
      loaderConfiguration.destination,
      "UserCancelled"
    );
    setLoaderConfiguration(undefined);
  }, [loaderConfiguration, telemetry, setLoaderConfiguration]);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const onLoaderCopy = React.useCallback(() => {
    if (!loaderConfiguration) {
      return;
    }
    telemetry.sendTryInAppEventCopy(
      loaderConfiguration.prompt,
      loaderConfiguration.destination,
      loaderConfiguration?.error
    );
  }, [loaderConfiguration, telemetry]);

  return {
    recentFiles,
    onHandoffFileSelectionMenuOpenChange,
    handoffFileSelectionMenuConfiguration,
    onLoaderCancel,
    loaderConfiguration,
    onLoaderCopy,
    onTryInAppBadgeAction,
    onTryByCopyBadgeAction,
  };
};
